// src/pages/ScheduledScans.js
// « Surveillance » : un scan automatique par semaine sur chaque domaine vérifié (plans Pro et Enterprise),
// avec un email si le score baisse ou si un nouveau point critique apparaît.
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, Play, Pause, Trash2, Loader2, Lock, Globe, ArrowRight } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const PAUSE_REASONS = {
  domain_unverified: "suspendu : l'enregistrement DNS de vérification est introuvable",
  plan: 'suspendu : plan Pro ou Enterprise requis',
};

export default function ScheduledScans() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const plan = user?.subscription_plan || 'free';
  const allowed = plan === 'pro' || plan === 'enterprise';

  const [scans, setScans] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [s, d] = await Promise.all([
        axios.get(`${API_URL}/api/scheduled-scans`, { headers }),
        axios.get(`${API_URL}/api/domains`, { headers }),
      ]);
      setScans(s.data || []);
      setDomains(d.data || []);
    } catch {
      toast.error('Impossible de charger la surveillance');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const headers = { Authorization: `Bearer ${token}` };
  const scheduled = new Set(scans.map((s) => s.domain));
  const available = domains.filter((d) => d.verified && !scheduled.has(d.domain));

  const create = async () => {
    if (!selected) return;
    setBusy('create');
    try {
      await axios.post(`${API_URL}/api/scheduled-scans`, { target_domain: selected }, { headers });
      toast.success(`Surveillance de ${selected} activée — premier scan à la prochaine tournée du matin`);
      setSelected('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (s) => {
    setBusy(s.schedule_id);
    try {
      const res = await axios.patch(`${API_URL}/api/scheduled-scans/${s.schedule_id}/toggle`, {}, { headers });
      toast.success(res.data.is_active ? 'Surveillance réactivée' : 'Surveillance suspendue');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Arrêter la surveillance de ${s.domain} ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/scheduled-scans/${s.schedule_id}`, { headers });
      await load();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 max-w-4xl">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono uppercase tracking-tighter">Surveillance</h1>
          <p className="text-zinc-500 text-sm mt-2 max-w-2xl">
            Chaque semaine, CyberGuard relance automatiquement l'audit de vos domaines vérifiés et vous prévient par email
            si le score baisse ou si un nouveau point critique apparaît.
          </p>
        </div>

        {!allowed ? (
          <div className="bg-zinc-900 border border-zinc-800 p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Lock className="w-6 h-6 text-zinc-500 shrink-0" />
              <div>
                <p className="text-white font-mono font-bold text-sm">Surveillance hebdomadaire — plans Pro et Enterprise</p>
                <p className="text-zinc-500 text-xs mt-1">Un scan par semaine et par domaine, sans consommer votre quota, avec alerte email en cas de dégradation.</p>
              </div>
            </div>
            <button onClick={() => navigate('/billing')} className="bg-blue-600 hover:bg-blue-700 px-5 py-2 font-mono text-xs uppercase text-white shrink-0">
              Voir les plans →
            </button>
          </div>
        ) : loading ? (
          <p className="text-zinc-500 font-mono text-xs">Chargement…</p>
        ) : (
          <>
            {available.length > 0 ? (
              <div className="flex flex-col sm:flex-row gap-3">
                <select value={selected} onChange={(e) => setSelected(e.target.value)}
                  className="flex-1 bg-black border border-zinc-800 p-3 text-white font-mono focus:border-blue-500 outline-none">
                  <option value="">Choisir un domaine vérifié…</option>
                  {available.map((d) => <option key={d.domain_id} value={d.domain}>{d.domain}</option>)}
                </select>
                <button onClick={create} disabled={!selected || busy === 'create'}
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-5 py-3 font-mono text-xs uppercase tracking-widest text-white">
                  {busy === 'create' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Surveiller chaque semaine
                </button>
              </div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 p-5 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-zinc-400 text-sm flex items-center gap-2">
                  <Globe className="w-4 h-4 text-zinc-500" />
                  {domains.some((d) => d.verified) ? 'Tous vos domaines vérifiés sont déjà surveillés.' : "Vérifiez d'abord un domaine pour pouvoir le surveiller."}
                </p>
                <Link to="/domains" className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 font-mono text-xs uppercase">
                  Mes domaines <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            )}

            {scans.length > 0 && (
              <ul className="space-y-3">
                {scans.map((s) => (
                  <li key={s.schedule_id} className="bg-zinc-900 border border-zinc-800 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-white font-bold">{s.domain}</span>
                        <span className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${s.is_active ? 'border-green-500/40 text-green-400' : 'border-zinc-700 text-zinc-500'}`}>
                          {s.is_active ? 'Hebdomadaire' : 'Suspendu'}
                        </span>
                      </div>
                      <p className="text-zinc-500 text-xs font-mono">
                        Dernier scan : {fmtDate(s.last_run_at)}
                        {s.last_score !== null && s.last_score !== undefined && (
                          <> · score {s.last_audit_id ? <Link to={`/audits/${s.last_audit_id}`} className="text-blue-400 hover:underline">{s.last_score}/100</Link> : `${s.last_score}/100`}</>
                        )}
                        {s.is_active && <> · prochain : {s.last_run_at ? fmtDate(s.next_run_at) : 'à la prochaine tournée du matin'}</>}
                      </p>
                      {!s.is_active && s.paused_reason && <p className="text-amber-400 text-xs">{PAUSE_REASONS[s.paused_reason] || 'suspendu'}</p>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => toggle(s)} disabled={busy === s.schedule_id}
                        className="inline-flex items-center gap-2 border border-zinc-700 hover:border-blue-500 px-3 py-2 font-mono text-[10px] uppercase text-zinc-300">
                        {busy === s.schedule_id ? <Loader2 className="w-3 h-3 animate-spin" /> : s.is_active ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                        {s.is_active ? 'Suspendre' : 'Réactiver'}
                      </button>
                      <button onClick={() => remove(s)} className="p-2 border border-zinc-800 hover:border-red-500 text-zinc-500 hover:text-red-400" aria-label={`Arrêter la surveillance de ${s.domain}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
